import { COUNTRIES } from '../create/countries';
import { LANGUAGES } from '../create/languages';

/**
 * Guessing a project's market so the user does not have to pick one during onboarding.
 *
 * Everything downstream of step 1 -- profiling the brand, generating topics, generating prompts
 * -- refuses to run without a country and a language, so leaving them empty would turn step 2
 * into the dead end this flow exists to remove. A guess the user can see and correct beats an
 * empty pair of selects, and these guesses are only ever consulted last, behind the project's
 * own market and the user's previous pick.
 */

/**
 * Two-letter TLDs that are sold as generic domains rather than used for their country.
 *
 * Each of these really is a country code, so a plain ISO lookup would place a .io startup in
 * the British Indian Ocean Territory and an .ai company in Anguilla. Guessing nothing is better
 * than guessing those.
 */
const GENERIC_TLDS = new Set([
    'ai',
    'cc',
    'co',
    'fm',
    'gg',
    'io',
    'ly',
    'me',
    'sh',
    'st',
    'to',
    'tv',
    'ws',
]);

/** TLDs whose country is not spelled the way ISO 3166 spells it. */
const TLD_COUNTRY_ALIASES = { uk: 'GB' };

function knownCountry(code) {
    return COUNTRIES.find((country) => country.value === code) ?? null;
}

function knownLanguage(code) {
    return LANGUAGES.some((language) => language.value === code) ? code : null;
}

/** The country entry a domain's TLD points at, or null when it points nowhere useful. */
function countryFromDomain(domain) {
    const host = String(domain ?? '')
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/\/.*$/, '');

    const tld = host.split('.').pop();
    if (!tld || tld.length !== 2 || GENERIC_TLDS.has(tld)) return null;

    return knownCountry(TLD_COUNTRY_ALIASES[tld] ?? tld.toUpperCase());
}

/** Market implied by a domain's TLD: `.de` means Germany, and Germany brings German with it. */
export function marketFromDomain(domain) {
    const country = countryFromDomain(domain);
    if (!country) return {};

    return {
        country: country.value,
        // A handful of COUNTRIES entries carry no primary language; those contribute a country
        // only, and the language falls through to the next guess.
        language: knownLanguage(country.language) ?? undefined,
    };
}

/**
 * Market implied by the browser's own locale, e.g. `pt-BR` -> Brazil, Brazilian Portuguese.
 *
 * The full tag is tried before the bare language because a few of the languages on offer are
 * regional variants, and `pt-BR` is a better answer than `pt` when the browser said so.
 */
export function marketFromLocale() {
    const tag = navigator?.languages?.[0] ?? navigator?.language;
    if (!tag) return {};

    const [base, region] = String(tag).split('-');
    const language = knownLanguage(String(tag).toUpperCase()) ?? knownLanguage(base?.toUpperCase());
    const country = region ? knownCountry(region.toUpperCase()) : null;

    return {
        country: country?.value ?? undefined,
        language: language ?? undefined,
    };
}

/**
 * Best guess at a market for a domain, TLD first and the browser's locale behind it.
 *
 * The two are merged field by field rather than taken whole: a `.de` domain opened in an
 * English browser should still be profiled in Germany, and a `.com` domain should still pick up
 * whatever the browser can say about the user.
 */
export function guessMarket(domain) {
    const fromDomain = marketFromDomain(domain);
    const fromLocale = marketFromLocale();

    return {
        country: fromDomain.country ?? fromLocale.country ?? '',
        language: fromDomain.language ?? fromLocale.language ?? '',
    };
}
